import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, Divider, Stack, Typography } from "@mui/material";
import { statusOptions } from "data/globalData";
import { useEffect, useRef, useState } from "react";
import { finalMRPColumn } from "./data";

import axios from "axios";
import { PimDashboardState } from "context/PimDashboardContext";
import RenderAttributeInputs from "components/PimDashboard/DashboardComponents/Designer/CreateStyle/RenderAttributes/RenderAttributeInputs";
import { attrGroupLinkedTables, formatColumnValue } from "components/PimDashboard/data";
import { GlobalState } from "context/GlobalContext";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { grey } from "@mui/material/colors";

import { fabricCategoryColumn, fabricFinalColumns } from "components/PimDashboard/DashboardComponents/Sourcing/FabricMaster/data";
import { footwearMaterialColumns } from "components/PimDashboard/DashboardComponents/Sourcing/FootwearMaterialMaster/data";
import ColorPaletteCogsTable from "./ColorPaletteCogsTable/ColorPaletteCogsTable";
import { MRP_SHEET_API } from "../data";

const seedFromTableRow = (row) => {
	if (!row) return undefined;
	return {
		[attrGroupLinkedTables.style.key]: row,
		[attrGroupLinkedTables.style_details.key]: {
			[finalMRPColumn.key]: row?.[finalMRPColumn.key],
		},
		[attrGroupLinkedTables.color.key]: [],
	};
};

const EditDetailsModal = ({
	modal,
	setModal,
	selectedStyle,
	setSelectedStyle,
	saveCallback,
	canEdit = true,
	canPublish = false,
}) => {
	const { setSnackBar, setSnackBarOpen, snackBar, setLoadingOpen } = GlobalState();
	const { allAttributes, attrGroupsWithAttr } = PimDashboardState();
	const requestIdRef = useRef(0);

	const [fullStyleData, setFullStyleData] = useState();
	const [updatedDetails, setUpdatedDetails] = useState({});
	const [loading, setLoading] = useState(true);
	const [colorsLoading, setColorsLoading] = useState(false);
	const [alreadyInwarded, setAlreadyInwarded] = useState(false);

	const handleModalClose = () => {
		setModal(false);
		setSelectedStyle();
		setFullStyleData(undefined);
		setLoading(true);
		setColorsLoading(false);
		setAlreadyInwarded(false);
	};

	const handleFabricInputChange = (dataObj) => {
		if (typeof dataObj === "function") dataObj = dataObj(fullStyleData);

		let updatedFabricValues = allAttributes
			.filter(
				(item) =>
					item.group_id === "fabric_details" &&
					!fabricFinalColumns[item.key] &&
					item.key !== fabricCategoryColumn.key &&
					!footwearMaterialColumns[item.key],
			)
			.reduce((acc, curr) => {
				acc[curr.key] = dataObj[attrGroupLinkedTables.style_details.key][curr.key];
				return acc;
			}, {});

		setUpdatedDetails((prev) => ({ ...prev, ...updatedFabricValues }));
		setFullStyleData((prev) => ({
			...prev,
			[attrGroupLinkedTables.style_details.key]: { ...prev[attrGroupLinkedTables.style_details.key], ...updatedFabricValues },
		}));
	};

	const handleFabricSaveClick = async (publish = false) => {
		const finalMrp = fullStyleData?.[attrGroupLinkedTables.style_details.key]?.[finalMRPColumn.key];

		if (finalMrp === "" || finalMrp == null || Number.isNaN(Number(finalMrp))) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, severity: "error", message: "Please Add MRP" });
			return;
		}

		const body = {
			style_id: selectedStyle?.style_id,
			final_mrp: Number(finalMrp),
		};
		try {
			setLoadingOpen(true);
			const response = await axios.post(`${MRP_SHEET_API}/bulk_upload_final_mrp`, {
				mark_as_published: publish,
				rows: [body],
			});

			const processed = response?.data?.result?.processed ?? 1;
			const successful = response?.data?.result?.successful ?? 1;
			const failed = Math.max(processed - successful, 0);

			saveCallback({
				...selectedStyle,
				[finalMRPColumn.key]: body.final_mrp,
				status: publish ? statusOptions.PUBLISHED.key : selectedStyle.status,
			});
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				severity: failed > 0 ? "warning" : "success",
				message: failed > 0 ? `${successful} MRP rows uploaded successfully, ${failed} failed` : "Data updated Successfully",
			});
			handleModalClose();
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, severity: "error", message: parseErrorMessage(error) });
		} finally {
			setLoadingOpen(false);
		}
	};

	const fetchStyleDetails = async (styleId) => {
		const requestId = ++requestIdRef.current;
		try {
			const res = await axios.get(`${MRP_SHEET_API}/style_by_id/${styleId}`);
			if (requestId !== requestIdRef.current) return;
			setFullStyleData(res.data);
			setAlreadyInwarded(res.data?.already_inwarded === true);
			setUpdatedDetails(
				allAttributes
					.filter((attr) => attr.group_id === "fabric_details")
					.reduce((acc, row) => {
						acc[row.key] = res.data?.[attrGroupLinkedTables.style_details.key]?.[row.key];
						return acc;
					}, {}),
			);
		} catch (error) {
			if (requestId !== requestIdRef.current) return;
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, severity: "error", message: parseErrorMessage(error) });
		} finally {
			if (requestId === requestIdRef.current) {
				setLoading(false);
				setColorsLoading(false);
			}
		}
	};

	useEffect(() => {
		if (!modal || !selectedStyle?.style_id) return;

		// Paint immediately from the table row; refresh with lean API in background.
		const seeded = seedFromTableRow(selectedStyle);
		setFullStyleData(seeded);
		setAlreadyInwarded(false);
		setLoading(false);
		setColorsLoading(true);
		fetchStyleDetails(selectedStyle.style_id);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modal, selectedStyle?.style_id]);

	const styleStatus = fullStyleData?.[attrGroupLinkedTables.style.key]?.status;
	const mrpLocked = alreadyInwarded === true;
	const canChangeMrp = canEdit && !mrpLocked && !colorsLoading;

	return (
		<Dialog fullWidth maxWidth="xl" open={modal} onClose={() => handleModalClose()} scroll="body" keepMounted={false}>
			{loading || !fullStyleData ? (
				<Box width="100%" height={300} display="flex" justifyContent="center" alignItems="center">
					<CircularProgress color="inherit" />
				</Box>
			) : (
				<>
					<Box
						display="flex"
						width="100%"
						flexDirection={{ xs: "column", md: "row" }}
						justifyContent="space-around"
						columnGap={2}
						rowGap={2}>
						<Stack
							width={{ xs: "100%", md: "28%" }}
							minWidth={{ md: 260 }}
							flex={{ md: "0 0 28%" }}
							p={2}
							boxSizing="border-box"
							spacing={1.25}
							sx={{ minWidth: 0 }}>
							<Typography variant="h6">Edit Buying Details</Typography>
							{mrpLocked && <Alert severity="warning">Already Inwarded. MRP cannot be changed.</Alert>}

							<Box sx={{ width: "100%" }}>
								<RenderAttributeInputs
									columns={[
										...allAttributes.reduce((acc, row) => {
											if (row?.key === finalMRPColumn.key) acc.push({ ...row, ...finalMRPColumn });
											return acc;
										}, []),
									]}
									setData={handleFabricInputChange}
									data={fullStyleData}
									attrGrp={attrGroupsWithAttr["fabric_details"]}
									itemWidth="90%"
									width={"100%"}
									setErrorObj={() => {}}
									errObj={{}}
									disabledColumns={canChangeMrp ? [] : [finalMRPColumn.key]}
								/>
							</Box>

							<Box sx={{ position: "relative" }}>
								<ColorPaletteCogsTable colors={fullStyleData?.[attrGroupLinkedTables.color.key]} />
								{colorsLoading && (
									<Box
										sx={{
											position: "absolute",
											inset: 0,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											bgcolor: "rgba(255,255,255,0.55)",
											borderRadius: 1.5,
										}}>
										<CircularProgress size={22} />
									</Box>
								)}
							</Box>
						</Stack>
						<Divider flexItem orientation="vertical" sx={{ display: { xs: "none", md: "block" } }} />
						<Stack
							width={{ xs: "100%", md: "auto" }}
							flex={1}
							direction="row"
							flexWrap="wrap"
							spacing={"1%"}
							sx={{ minWidth: 0 }}>
							<Typography variant="h6" sx={{ width: "100%", mb: 1 }}>
								Style Details
							</Typography>
							{allAttributes
								.filter((row) => row.linked_table === attrGroupLinkedTables.style.key)
								.map((row) => {
									return (
										<Box
											width={"30%"}
											key={row?.name}
											display="flex"
											border={`1px solid white`}
											boxSizing="border-box">
											<Typography
												variant="subtitle2"
												color={grey[200]}
												sx={{
													width: "38%",
													p: "4px 2%",
													background: "#000",
													overflow: "hidden",
													borderRadius: "1px 0px 0px 1px",
												}}>
												{row?.name}
											</Typography>
											<Typography
												variant="subtitle2"
												sx={{
													width: "55%",
													p: "4px 2%",
													background: grey[200],
													fontWeight: 400,
													overflow: "hidden",
													borderRadius: " 0px 1px 1px 0px",
												}}>
												{fullStyleData?.[row.linked_table]?.[row?.key] !== undefined
													? formatColumnValue(fullStyleData?.[row.linked_table][row?.key], row)
													: ""}
											</Typography>
										</Box>
									);
								})}
							<Typography variant="h6" sx={{ width: "100%", mb: 1 }}>
								Suggestions
							</Typography>
							{allAttributes
								.filter((row) => row.group_id === "suggestions")
								.map((row) => {
									return (
										<Box
											width={"30%"}
											key={row?.name}
											display="flex"
											border={`1px solid white`}
											boxSizing="border-box">
											<Typography
												variant="subtitle2"
												color={grey[200]}
												sx={{
													width: "38%",
													p: "3px 2%",
													background: "#000",
													overflow: "hidden",
													borderRadius: "1px 0px 0px 1px",
												}}>
												{row?.name}
											</Typography>
											<Typography
												variant="subtitle2"
												sx={{
													width: "55%",
													p: "3px 2%",
													background: grey[200],
													fontWeight: 400,
													overflow: "hidden",
													borderRadius: " 0px 1px 1px 0px",
												}}>
												{fullStyleData?.[row.linked_table]?.[row?.key] !== undefined
													? formatColumnValue(fullStyleData?.[row.linked_table][row?.key], row)
													: ""}
											</Typography>
										</Box>
									);
								})}
						</Stack>
					</Box>

					<DialogActions>
						<Button variant="outlined" onClick={() => handleModalClose()}>
							Cancel
						</Button>
						{canChangeMrp && (
							<Button autoFocus variant="contained" sx={{ mr: 1 }} onClick={() => handleFabricSaveClick(false)}>
								Save
							</Button>
						)}
						{styleStatus === statusOptions.PUBLISHED.key ? (
							<Button variant="contained" color="success" disableFocusRipple disableRipple disableElevation>
								Published
							</Button>
						) : (
							canEdit &&
							canPublish && (
								<Button
									autoFocus
									variant="contained"
									sx={{ mr: 1 }}
									onClick={() => handleFabricSaveClick(true)}
									disabled={colorsLoading || styleStatus !== statusOptions.MRP_PENDING.key}>
									Save And Publish
								</Button>
							)
						)}
					</DialogActions>
				</>
			)}
		</Dialog>
	);
};

export default EditDetailsModal;
